// src/utils/error.js - Normalize API/axios errors into user-displayable strings

/**
 * Turn unknown error shapes (axios errors, backend structured errors, etc.) into a safe string.
 * This prevents React crashes from accidentally rendering an object.
 */
export function getErrorMessage(err, fallback = 'Something went wrong') {
  // If caller already passed a usable value
  if (typeof err === 'string') return err;
  if (err == null) return fallback;

  // Standard Error
  if (err instanceof Error && typeof err.message === 'string' && err.message.trim()) {
    return err.message;
  }

  // Axios-style: err.response.data.{error|message}
  const data = err?.response?.data;
  const candidate = data?.error ?? data?.message ?? err?.message ?? err;

  return coerceToMessage(candidate, fallback);
}

function coerceToMessage(value, fallback) {
  if (value == null) return fallback;
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);

  // Backend structured error object: { message, code, status, ... }
  if (typeof value === 'object') {
    // Node backend standardized errors: { message, code, status, details? }
    // express-validator details shape: [{ msg, path, ... }, ...]
    if (Array.isArray(value.details) && value.details.length > 0) {
      const msgs = value.details
        .map((d) => {
          const field = typeof d?.path === 'string' ? d.path : typeof d?.param === 'string' ? d.param : null;
          const msg = typeof d?.msg === 'string' ? d.msg : typeof d?.message === 'string' ? d.message : null;
          if (!msg) return null;
          return field ? `${field}: ${msg}` : msg;
        })
        .filter(Boolean);

      if (msgs.length > 0) return msgs.join('\n');
    }

    if (typeof value.message === 'string' && value.message.trim()) return value.message;
    if (typeof value.error === 'string' && value.error.trim()) return value.error;
    if (typeof value.detail === 'string' && value.detail.trim()) return value.detail;
    if (typeof value.title === 'string' && value.title.trim()) return value.title;

    // Last resort: stringify (avoid crashing the UI)
    try {
      return JSON.stringify(value);
    } catch {
      return fallback;
    }
  }

  return fallback;
}


